import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from 'react';
import firebase from '../config/firebase.web';
import UserService, { NotAuthorizedError } from '../services/userService';
import { UserProfile, UserRole } from '../types/UserProfile';
import { ErrorLogger } from '../utils/errorLogger';

const NOT_AUTHORIZED_MESSAGE =
  "This account isn't authorized. Ask your supervisor to add you.";
const AUTH_FAILED_MESSAGE =
  "Couldn't verify your access. Please try signing in again.";

interface User {
  uid: string;
  email: string | null;
  displayName: string | null;
  photoURL: string | null;
}

interface AuthContextType {
  user: User | null;
  userProfile: UserProfile | null;
  role: UserRole | null;
  loading: boolean;
  authError: string | null;
  signOut: () => Promise<void>;
  signInWithGoogle: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState<string | null>(null);

  const signOut = async () => {
    await firebase.auth().signOut();
  };

  useEffect(() => {
    const auth = firebase.auth();
    return auth.onAuthStateChanged(async firebaseUser => {
      if (firebaseUser) {
        setUser({
          uid: firebaseUser.uid,
          email: firebaseUser.email,
          displayName: firebaseUser.displayName,
          photoURL: firebaseUser.photoURL,
        });

        // Resolve role from the allowlist. Only allowlisted users (or an
        // existing profile) are permitted — anyone else is signed out.
        try {
          const profile = await UserService.resolveProfile(
            firebaseUser.uid,
            firebaseUser.email || '',
            firebaseUser.displayName || '',
          );
          setUserProfile(profile);
          setAuthError(null);
        } catch (error) {
          const notAuthorized = error instanceof NotAuthorizedError;
          if (!notAuthorized) {
            ErrorLogger.logError(error as any, 'AuthContext.web.resolveProfile');
          }
          setUserProfile(null);
          setAuthError(
            notAuthorized ? NOT_AUTHORIZED_MESSAGE : AUTH_FAILED_MESSAGE,
          );
          await signOut();
        }
      } else {
        setUser(null);
        setUserProfile(null);
      }
      setLoading(false);
    });
  }, []);

  const signInWithGoogle = async () => {
    setAuthError(null);
    const auth = firebase.auth();
    const provider = new firebase.auth.GoogleAuthProvider();
    try {
      await auth.signInWithPopup(provider);
    } catch (err: unknown) {
      const error = err as { code?: string };
      // Only fall back to a full-page redirect when the browser actually
      // blocked the popup. If the user closed it themselves, treat it as a
      // cancel rather than forcing a redirect (which is the flow that loops
      // on cross-origin custom domains).
      if (error.code === 'auth/popup-blocked') {
        await auth.signInWithRedirect(provider);
      } else if (error.code === 'auth/popup-closed-by-user') {
        return;
      } else {
        throw err;
      }
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        userProfile,
        role: userProfile?.role ?? null,
        loading,
        authError,
        signInWithGoogle,
        signOut,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
